/**
 * Sharing the report — the sheet between the ending and somebody else's timeline.
 *
 * It leads with the *picture*, because the picture is the thing being shared: a
 * sheet that led with a caption field would be asking the learner to write
 * something before they know what they are attaching. The caption comes
 * pre-filled with a sentence that is true (the book's name and the score) and is
 * editable, which is the difference between a share and a generated message.
 *
 * Three decisions shape it.
 *
 *  - **One drawing, two destinations.** Posting to the feed and saving the picture
 *    both capture the same mounted card, so the file in a camera roll and the file
 *    in a post are the same file. The card is captured *at post size* (see
 *    `#/lib/flashbookReportImage`), never at the size the sheet happens to preview
 *    it at — the sheet shows a copy scaled to its own column and photographs a
 *    second one laid out at the card's size, because a snapshot of a scaled-down
 *    card is a photograph of a thumbnail.
 *  - **It says when it cannot post.** The primary action needs an account, and a
 *    learner who is signed out is told that in place — with the save path still
 *    working, because the picture does not need a session.
 *  - **A failure keeps the work.** Nothing is closed and nothing is thrown away
 *    when a post fails: the card, the caption and the sheet stay exactly where
 *    they were, with a line saying what happened.
 */
import {useMemo, useRef, useState} from 'react'
import {
  StyleSheet,
  Text,
  TextInput,
  useWindowDimensions,
  View,
} from 'react-native'
import {requestMediaLibraryPermissionsAsync} from 'expo-image-picker'
import {createAssetAsync} from 'expo-media-library'
import {type AtpAgent} from '@atproto/api'
import {Trans, useLingui} from '@lingui/react/macro'

import {uploadBlob} from '#/lib/api'
import {type FlashBookDirection} from '#/lib/flashbookDirection'
import {
  buildFlashBookReport,
  type FlashBookReportModel,
} from '#/lib/flashbookReport'
import {
  flashbookReportPostRecord,
  REPORT_CARD_SIZE,
  REPORT_IMAGE_SIZES,
  reportCardGraded,
} from '#/lib/flashbookReportCard'
import {captureReportImage} from '#/lib/flashbookReportImage'
import {atoms as a, useTheme, web} from '#/alf'
import {Button, ButtonText} from '#/components/Button'
import * as Dialog from '#/components/Dialog'
import {FlashBookReportCard} from '#/components/flashbooks/FlashBookReportCard'
import * as Toast from '#/components/Toast'
import {IS_NATIVE, IS_WEB} from '#/env'
import {type FlashBookDesign} from '#/themes-flashbook/design'
import {
  type FlashBookPage,
  type FlashBookPageResult,
  type FlashBookThemeTokens,
} from '#/types/flashbook'

type Props = {
  control: Dialog.DialogControlProps
  /**
   * Who is posting, or `null` when nobody is signed in.
   *
   * Taken as a prop rather than read from the session context on purpose: a
   * dialog renders through the app's portal on the web, which is a different
   * tree from the reader that opened it — and a sheet that could not see the
   * session there would offer a learner a post button that does nothing.
   */
  agent: AtpAgent | null
  bookTitle: string
  pages: readonly FlashBookPage[]
  pageResults: readonly FlashBookPageResult[]
  elapsedMs: number
  themeTokens: FlashBookThemeTokens
  design?: FlashBookDesign
  direction?: FlashBookDirection
  /** Which read of the book this was, when the register knows. */
  readNumber?: number
}

export function FlashBookReportShareSheet({
  control,
  agent,
  bookTitle,
  pages,
  pageResults,
  elapsedMs,
  themeTokens,
  design,
  direction,
  readNumber,
}: Props) {
  const {t: l} = useLingui()
  const t = useTheme()
  const cardRef = useRef<View | null>(null)

  const model = useMemo(
    () => buildFlashBookReport({pages, pageResults, elapsedMs}),
    [pages, pageResults, elapsedMs],
  )
  // The read ended now, so the card is dated now. Built once per mount rather than
  // per render: a date recomputed on every keystroke in the caption field would be
  // the same date, but a card that re-rendered its own footer on typing would be a
  // card that redraws under the reader's hands.
  const endedAt = useMemo(() => new Date(), [])

  return (
    <Dialog.Outer
      control={control}
      nativeOptions={{preventExpansion: true}}
      webOptions={{alignCenter: true}}>
      <Dialog.Handle />
      <Dialog.ScrollableInner
        style={web({maxWidth: 520})}
        label={l`Share your report`}>
        <SheetBody
          control={control}
          cardRef={cardRef}
          bookTitle={bookTitle}
          model={model}
          themeTokens={themeTokens}
          design={design}
          direction={direction}
          readNumber={readNumber}
          endedAt={endedAt}
          agent={agent}
          theme={t}
        />
        <Dialog.Close />
      </Dialog.ScrollableInner>
    </Dialog.Outer>
  )
}

/** How much of a phone's width the sheet leaves around the preview. */
const PREVIEW_INSET = 96

type Phase = 'idle' | 'posting' | 'saving'

function SheetBody({
  control,
  cardRef,
  bookTitle,
  model,
  themeTokens,
  design,
  direction,
  readNumber,
  endedAt,
  agent,
  theme,
}: {
  control: Dialog.DialogControlProps
  cardRef: React.RefObject<View | null>
  bookTitle: string
  model: FlashBookReportModel
  themeTokens: FlashBookThemeTokens
  design?: FlashBookDesign
  direction?: FlashBookDirection
  readNumber?: number
  endedAt: Date
  agent: AtpAgent | null
  theme: ReturnType<typeof useTheme>
}) {
  const {t: l} = useLingui()
  const graded = reportCardGraded(model)
  const [phase, setPhase] = useState<Phase>('idle')
  const [error, setError] = useState<string | null>(null)

  const suggested = useMemo(
    () =>
      graded
        ? l`Just finished “${bookTitle}” — ${model.score.pct}% of the points.`
        : l`Just finished “${bookTitle}” on Taderok.`,
    [bookTitle, graded, l, model.score.pct],
  )
  const [caption, setCaption] = useState(suggested)

  /**
   * What the picture shows, for the reader who cannot see it.
   *
   * Written from the model rather than from the caption, because the caption can be
   * edited down to nothing and the picture still holds every number on it.
   */
  const alt = useMemo(
    () =>
      graded
        ? l`A report card for “${bookTitle}”: ${model.score.pct}% of the points, ${model.counts.correct} of ${model.counts.exercises} correct.`
        : l`A report card for “${bookTitle}”, read to the end.`,
    [
      bookTitle,
      graded,
      l,
      model.counts.correct,
      model.counts.exercises,
      model.score.pct,
    ],
  )

  /** The picture, at the largest size that fits a post's blob. */
  async function draw() {
    const image = await captureReportImage(cardRef)
    if (!image.fits) {
      // A real failure with a real alternative: the smallest capture is still a
      // picture the learner can keep, it is only the post that cannot take it.
      throw new Error(l`The picture came out too large to post.`)
    }
    return image
  }

  async function onPost() {
    if (phase !== 'idle') return
    setPhase('posting')
    setError(null)
    try {
      if (!agent) throw new Error(l`Sign in to post this.`)
      const image = await draw()
      // The encoding is stated rather than sniffed: a capture is a PNG on both
      // platforms, and a blob uploaded without a type is one the server refuses.
      const uploaded = await uploadBlob(agent, image.uri, 'image/png')
      const record = flashbookReportPostRecord({
        text: caption.trim(),
        createdAt: new Date().toISOString(),
        image: uploaded.data.blob,
        alt,
      })
      await agent.com.atproto.repo.createRecord({
        repo: agent.session!.did,
        collection: 'app.bsky.feed.post',
        record,
      })
      Toast.show(l`Your report is in your feed.`)
      control.close()
    } catch (e: unknown) {
      // Kept, not thrown away: the caption the learner wrote is the only
      // irreplaceable thing on this screen.
      console.error('Failed to share a flashbook report', e)
      setError(
        e instanceof Error && e.message
          ? e.message
          : l`That did not go through. Try again.`,
      )
    } finally {
      setPhase('idle')
    }
  }

  async function onSave() {
    if (phase !== 'idle') return
    setPhase('saving')
    setError(null)
    try {
      const image = await captureReportImage(cardRef)
      if (IS_NATIVE) {
        const permission = await requestMediaLibraryPermissionsAsync()
        if (!permission.granted) {
          setError(
            l`Your photo library needs permission before the card can be saved.`,
          )
          return
        }
        await createAssetAsync(
          image.uri.startsWith('file://') ? image.uri : `file://${image.uri}`,
        )
      } else {
        const link = document.createElement('a')
        link.setAttribute('href', image.uri)
        link.setAttribute(
          'download',
          `${bookTitle.replace(/[^\p{L}\p{N}]+/gu, '-').slice(0, 40) || 'flashbook'}-report.png`,
        )
        link.click()
      }
      Toast.show(
        IS_WEB ? l`Report downloaded.` : l`Report saved to your photos.`,
      )
    } catch (e: unknown) {
      console.error('Failed to save a flashbook report', e)
      setError(l`That did not save. Try again.`)
    } finally {
      setPhase('idle')
    }
  }

  const busy = phase !== 'idle'
  // A session and not merely an agent: the app holds an agent from the moment it
  // starts, and posting is the one thing here that needs an account.
  const canPost = !!agent?.session

  return (
    <View testID="flashbook-report-share-sheet" style={[a.gap_2xl]}>
      <View style={[a.gap_sm]}>
        <Text style={[a.font_bold, a.text_2xl]}>
          <Trans>Share your report</Trans>
        </Text>
        <Text
          style={[a.text_md, a.leading_snug, theme.atoms.text_contrast_medium]}>
          {canPost ? (
            <Trans>
              Post the card below to your feed, or keep it as a picture.
            </Trans>
          ) : (
            <Trans>
              Sign in to post this to your feed — you can still save the
              picture.
            </Trans>
          )}
        </Text>
      </View>

      <Preview
        cardRef={cardRef}
        bookTitle={bookTitle}
        model={model}
        themeTokens={themeTokens}
        design={design}
        direction={direction}
        readNumber={readNumber}
        endedAt={endedAt}
      />

      <View style={[a.gap_sm]}>
        <Text
          style={[a.text_sm, a.font_bold, theme.atoms.text_contrast_medium]}>
          <Trans>Caption</Trans>
        </Text>
        <TextInput
          testID="flashbook-report-share-caption"
          accessibilityLabel={l`Caption`}
          accessibilityHint={l`The words that go with the card in your post`}
          multiline
          value={caption}
          onChangeText={setCaption}
          placeholder={l`Say something about this read`}
          placeholderTextColor={theme.palette.contrast_500}
          maxLength={300}
          style={[
            a.w_full,
            a.rounded_md,
            a.p_md,
            a.text_md,
            theme.atoms.border_contrast_low,
            theme.atoms.bg_contrast_25,
            theme.atoms.text,
            {minHeight: 64, borderWidth: 1, textAlignVertical: 'top'},
          ]}
        />
      </View>

      {error && (
        <Text
          testID="flashbook-report-share-error"
          style={[a.text_sm, {color: theme.palette.negative_500}]}>
          {error}
        </Text>
      )}

      <View style={[a.gap_md]}>
        <Button
          testID="flashbook-report-share-post"
          label={l`Post to your feed`}
          size="large"
          color="primary"
          disabled={!canPost || busy}
          onPress={() => void onPost()}>
          <ButtonText>
            {phase === 'posting' ? (
              <Trans>Posting…</Trans>
            ) : (
              <Trans>Post to feed</Trans>
            )}
          </ButtonText>
        </Button>
        <Button
          testID="flashbook-report-share-save"
          label={l`Save the card`}
          size="large"
          color="secondary"
          disabled={busy}
          onPress={() => void onSave()}>
          <ButtonText>
            {phase === 'saving' ? (
              <Trans>Drawing…</Trans>
            ) : (
              <Trans>Save image</Trans>
            )}
          </ButtonText>
        </Button>
      </View>
    </View>
  )
}

/**
 * How far off the sheet the copy that gets photographed is kept.
 *
 * Far enough that no column could scroll to it, and to the *left* because a browser
 * does not extend a page's scrollable area that way: an element parked off the
 * right-hand side would give the sheet a horizontal scrollbar nobody could reason
 * about.
 */
const CAPTURE_OFFSET = -10_000

/**
 * The card, twice: once to look at, once to photograph.
 *
 * The sheet's column is narrower than the card on a phone, so the copy the learner
 * sees is scaled down with a transform. That is the reason for the second copy: a
 * transform is a paint-time thing, and a snapshotter photographs the layout it is
 * handed — so a capture of the preview is a capture of a thumbnail, upscaled into a
 * soft picture. The host below is laid out at the card's own 480×600 and parked
 * off-screen, and it is *magnified* by the ratio of the largest picture to the
 * card, so the text in the raster is drawn at the size it will be read at rather
 * than redrawn from 480px of pixels. Smaller pictures are then made by scaling the
 * result down, which sharpens rather than blurs.
 *
 * Both copies are the same component with the same props, so what is posted is what
 * the learner was shown — the difference is only who is looking.
 */
function Preview({
  cardRef,
  bookTitle,
  model,
  themeTokens,
  design,
  direction,
  readNumber,
  endedAt,
}: {
  cardRef: React.RefObject<View | null>
  bookTitle: string
  model: FlashBookReportModel
  themeTokens: FlashBookThemeTokens
  design?: FlashBookDesign
  direction?: FlashBookDirection
  readNumber?: number
  endedAt: Date
}) {
  const {width} = useWindowDimensions()
  const scale = Math.min(
    1,
    Math.max(0.4, (width - PREVIEW_INSET) / REPORT_CARD_SIZE.width),
  )
  const captureScale = REPORT_IMAGE_SIZES[0].width / REPORT_CARD_SIZE.width

  return (
    <>
      <View
        testID="flashbook-report-share-preview"
        style={[
          a.self_center,
          a.rounded_md,
          a.overflow_hidden,
          {
            width: Math.round(REPORT_CARD_SIZE.width * scale),
            height: Math.round(REPORT_CARD_SIZE.height * scale),
          },
        ]}>
        <View
          style={{
            // Anchored, not centred: a transform scales about the middle by default,
            // which would leave the card half outside its own box.
            transformOrigin: 'top left',
            transform: [{scale}],
          }}>
          <FlashBookReportCard
            bookTitle={bookTitle}
            model={model}
            themeTokens={themeTokens}
            design={design}
            direction={direction}
            readNumber={readNumber}
            endedAt={endedAt}
          />
        </View>
      </View>

      {/**
         The copy that is photographed: never seen, never tapped, and read by
         nobody — a screen reader already has the card's numbers in the preview
         above it, and announcing them twice would be the same report twice.
         */}
      <View
        ref={cardRef}
        testID="flashbook-report-share-capture"
        aria-hidden
        importantForAccessibility="no-hide-descendants"
        pointerEvents="none"
        style={[
          styles.captureHost,
          {
            transformOrigin: 'top left',
            transform: [{scale: captureScale}],
          },
        ]}>
        <FlashBookReportCard
          bookTitle={bookTitle}
          model={model}
          themeTokens={themeTokens}
          design={design}
          direction={direction}
          readNumber={readNumber}
          endedAt={endedAt}
        />
      </View>
    </>
  )
}

const styles = StyleSheet.create({
  captureHost: {
    position: 'absolute',
    top: 0,
    left: CAPTURE_OFFSET,
    width: REPORT_CARD_SIZE.width,
    height: REPORT_CARD_SIZE.height,
  },
})
